import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, BookOpen, Landmark } from "lucide-react";
import { PageHeader, Card } from "@/components/common/PageHeader";
import { Loading } from "@/pages/_shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

function StatusPill({ status }) {
  const pub = status === "Published";
  return <span className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-bold uppercase ${pub ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{status}</span>;
}

const EMPTY_ARTICLE = { title: "", category: "Getting Started", excerpt: "", body: "", read_minutes: 4, author: "MMT Editorial" };
const EMPTY_OPP = { title: "", type: "Loan", amount: "", best_for: "", difficulty: "Intermediate", reports_to: "", summary: "", requirements: "" };

function ArticleEditor({ open, onClose, initial, categories, onSaved }) {
  const [form, setForm] = useState(EMPTY_ARTICLE);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;
  useEffect(() => { if (open) setForm(initial ? { ...EMPTY_ARTICLE, ...initial } : EMPTY_ARTICLE); }, [open, initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    setSaving(true);
    try {
      if (editing) await api.patch(`/learning/admin/articles/${initial.id}`, form);
      else await api.post("/learning/admin/articles", form);
      toast.success(editing ? "Article updated" : "Article created");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="article-editor">
        <DialogHeader><DialogTitle>{editing ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={form.title} onChange={set("title")} data-testid="article-title-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={set("category")} data-testid="article-category-select" className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Read minutes</Label><Input type="number" value={form.read_minutes} onChange={set("read_minutes")} /></div>
          </div>
          <div><Label>Author</Label><Input value={form.author} onChange={set("author")} /></div>
          <div><Label>Excerpt</Label><Textarea rows={2} value={form.excerpt} onChange={set("excerpt")} data-testid="article-excerpt-input" /></div>
          <div><Label>Body (Markdown)</Label><Textarea rows={12} value={form.body} onChange={set("body")} data-testid="article-body-input" className="font-mono text-[13px]" /></div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-[13px] font-semibold text-slate-700">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="article-save-btn" className="rounded-lg bg-[#080d1a] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OppEditor({ open, onClose, initial, types, onSaved }) {
  const [form, setForm] = useState(EMPTY_OPP);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;
  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY_OPP, ...initial, requirements: (initial.requirements || []).join("\n") } : EMPTY_OPP);
  }, [open, initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    setSaving(true);
    try {
      if (editing) await api.patch(`/opportunities/admin/${initial.id}`, form);
      else await api.post("/opportunities/admin", form);
      toast.success(editing ? "Opportunity updated" : "Opportunity created");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="opp-editor">
        <DialogHeader><DialogTitle>{editing ? "Edit opportunity" : "New opportunity"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={form.title} onChange={set("title")} data-testid="opp-title-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <select value={form.type} onChange={set("type")} data-testid="opp-type-select" className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Difficulty</Label>
              <select value={form.difficulty} onChange={set("difficulty")} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                {["Beginner", "Intermediate", "Advanced"].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Amount</Label><Input value={form.amount} onChange={set("amount")} placeholder="Up to $250,000" /></div>
            <div><Label>Reports to</Label><Input value={form.reports_to} onChange={set("reports_to")} placeholder="D&B, Experian Business" /></div>
          </div>
          <div><Label>Best for</Label><Input value={form.best_for} onChange={set("best_for")} /></div>
          <div><Label>Summary</Label><Textarea rows={3} value={form.summary} onChange={set("summary")} data-testid="opp-summary-input" /></div>
          <div><Label>Requirements (one per line)</Label><Textarea rows={4} value={form.requirements} onChange={set("requirements")} /></div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-[13px] font-semibold text-slate-700">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="opp-save-btn" className="rounded-lg bg-[#080d1a] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LearningAdmin() {
  const [articles, setArticles] = useState(null);
  const [categories, setCategories] = useState([]);
  const [opps, setOpps] = useState(null);
  const [oppTypes, setOppTypes] = useState([]);
  const [artEdit, setArtEdit] = useState(null);
  const [artOpen, setArtOpen] = useState(false);
  const [oppEdit, setOppEdit] = useState(null);
  const [oppOpen, setOppOpen] = useState(false);

  const loadArticles = useCallback(() => {
    api.get("/learning/admin/articles").then(({ data }) => { setArticles(data.articles); setCategories(data.categories); });
  }, []);
  const loadOpps = useCallback(() => {
    api.get("/opportunities/admin/all").then(({ data }) => { setOpps(data.opportunities); setOppTypes(data.types); });
  }, []);
  useEffect(() => { loadArticles(); loadOpps(); }, [loadArticles, loadOpps]);

  const toggleArticle = async (a) => {
    try { await api.post(`/learning/admin/articles/${a.id}/status`, { publish: a.status !== "Published" }); loadArticles(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };
  const deleteArticle = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await api.delete(`/learning/admin/articles/${a.id}`); loadArticles(); toast.success("Deleted");
  };
  const toggleOpp = async (o) => {
    try { await api.post(`/opportunities/admin/${o.id}/status`, { publish: o.status !== "Published" }); loadOpps(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };
  const deleteOpp = async (o) => {
    if (!window.confirm(`Delete "${o.title}"?`)) return;
    await api.delete(`/opportunities/admin/${o.id}`); loadOpps(); toast.success("Deleted");
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="learning-admin-page">
      <PageHeader
        eyebrow="Content"
        title="Learning & Opportunities"
        subtitle="Manage the public Learning guides and the Opportunities capital directory. Published items appear instantly on the marketing site."
      />
      <Tabs defaultValue="learning">
        <TabsList>
          <TabsTrigger value="learning" data-testid="admin-tab-learning"><BookOpen size={15} className="mr-1.5" /> Learning</TabsTrigger>
          <TabsTrigger value="opps" data-testid="admin-tab-opps"><Landmark size={15} className="mr-1.5" /> Opportunities</TabsTrigger>
        </TabsList>

        <TabsContent value="learning" className="mt-5 space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setArtEdit(null); setArtOpen(true); }} data-testid="new-article-btn" className="flex items-center gap-1.5 rounded-lg bg-[#080d1a] px-4 py-2 text-[13px] font-semibold text-white"><Plus size={15} /> New article</button>
          </div>
          {!articles ? <Loading /> : (
            <div className="space-y-2">
              {articles.map((a) => (
                <Card key={a.id} className="flex items-center justify-between gap-3 p-4" data-testid={`admin-article-${a.slug}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="truncate font-head text-[15px] font-bold text-slate-900">{a.title}</span><StatusPill status={a.status} /></div>
                    <div className="mt-0.5 text-[12px] text-slate-500">{a.category} · {a.read_minutes} min · /{a.slug}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button onClick={() => toggleArticle(a)} title={a.status === "Published" ? "Unpublish" : "Publish"} data-testid={`toggle-article-${a.slug}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">{a.status === "Published" ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                    <button onClick={() => { setArtEdit(a); setArtOpen(true); }} data-testid={`edit-article-${a.slug}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Pencil size={16} /></button>
                    <button onClick={() => deleteArticle(a)} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50"><Trash2 size={16} /></button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="opps" className="mt-5 space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setOppEdit(null); setOppOpen(true); }} data-testid="new-opp-btn" className="flex items-center gap-1.5 rounded-lg bg-[#080d1a] px-4 py-2 text-[13px] font-semibold text-white"><Plus size={15} /> New opportunity</button>
          </div>
          {!opps ? <Loading /> : (
            <div className="space-y-2">
              {opps.map((o) => (
                <Card key={o.id} className="flex items-center justify-between gap-3 p-4" data-testid={`admin-opp-${o.slug}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="truncate font-head text-[15px] font-bold text-slate-900">{o.title}</span><StatusPill status={o.status} /></div>
                    <div className="mt-0.5 text-[12px] text-slate-500">{o.type} · {o.amount} · {o.difficulty}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button onClick={() => toggleOpp(o)} title={o.status === "Published" ? "Unpublish" : "Publish"} data-testid={`toggle-opp-${o.slug}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">{o.status === "Published" ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                    <button onClick={() => { setOppEdit(o); setOppOpen(true); }} data-testid={`edit-opp-${o.slug}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Pencil size={16} /></button>
                    <button onClick={() => deleteOpp(o)} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50"><Trash2 size={16} /></button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ArticleEditor open={artOpen} onClose={() => setArtOpen(false)} initial={artEdit} categories={categories} onSaved={loadArticles} />
      <OppEditor open={oppOpen} onClose={() => setOppOpen(false)} initial={oppEdit} types={oppTypes.length ? oppTypes : ["Loan", "Line of Credit", "Grant", "Tradeline", "Equity"]} onSaved={loadOpps} />
    </div>
  );
}
